// Port of ios/Sources/ViewModels/MessageListDerivation.swift.
//
// Pure derivation of the message list's per-row presentation state (docs/VISUAL-DESIGN.md §4):
// consecutive-message grouping, day separators, and the unread separator. No DOM dependency so
// it is trivially testable, mirroring `ChannelListDerivation`.
import type { PostRecord } from './records';
import { civil, dateFormatter, dayKey, isSameDay, shiftedDayKey } from './time';
import { locale as uiLocale, t } from '../i18n/i18n';
import { MessageAttachments } from './messageAttachments';
import { SystemMessageDerivation } from './systemMessageDerivation';

export interface MessageListRow {
  post: PostRecord;
  /** First message of a group gets the avatar + header line; continuations don't. */
  isGroupStart: boolean;
  /** Non-null means a day-separator pill renders above this row, with this label. */
  dayLabel: string | null;
  /**
   * True means the unread-separator hairline renders above this row (at most one row ever
   * has this true).
   */
  showsUnreadSeparator: boolean;
}

export interface MessageListOptions {
  /** The only "now" (epoch ms) — see `dayLabel`. */
  now: number;
  /** IANA zone the calendar questions are asked in; undefined = the machine's. */
  timeZone?: string;
  /** BCP-47 tag; defaults to the UI language's (`i18n.locale()`). */
  locale?: string;
}

/**
 * A message starts a new group unless it's the same author as the previous post, within 5
 * minutes, with no day boundary or unread separator between them (docs/VISUAL-DESIGN.md §4:
 * "Group when: same author, within 5 minutes, no day boundary crossed").
 */
const groupWindowMillis = 5 * 60 * 1000;

function rows(posts: PostRecord[], unreadBoundary: number | null, options: MessageListOptions): MessageListRow[] {
  if (posts.length === 0) return [];
  const { now, timeZone } = options;

  // Only a genuine "you have unread messages from here down" marker when there is at least
  // one unread message — a channel opened for the very first time (lastViewedAt == 0, i.e.
  // nothing read yet) must not show the separator above the very first message. Note this
  // does NOT require the boundary to fall within the loaded page: if every loaded post is
  // already unread (the boundary is older than the oldest loaded post), the separator still
  // belongs above the first row.
  const showsSeparatorAtAll =
    unreadBoundary != null && unreadBoundary > 0 && posts[posts.length - 1].createAt > unreadBoundary;

  const result: MessageListRow[] = [];
  let separatorAlreadyShown = false;

  for (let index = 0; index < posts.length; index++) {
    const post = posts[index];
    const previous = index > 0 ? posts[index - 1] : null;

    const crossesDay = previous ? !isSameDay(previous.createAt, post.createAt, timeZone) : true;

    const showsUnread =
      showsSeparatorAtAll && !separatorAlreadyShown && unreadBoundary != null && post.createAt > unreadBoundary;
    if (showsUnread) separatorAlreadyShown = true;

    const sameAuthor = previous?.userId === post.userId;
    const withinWindow = previous ? post.createAt - previous.createAt <= groupWindowMillis : false;
    // A system notice is its own island: it never joins a group, and the message
    // after it starts fresh — a reply visually stacked under "@ana se unió" would read
    // as her first words.
    const systemBoundary =
      SystemMessageDerivation.isSystem(post.type) || (previous ? SystemMessageDerivation.isSystem(previous.type) : false);
    // An INTEGRATION post is never a continuation either — the webapp's `isConsecutivePost`
    // rule. Every webhook post wears its own name and icon (`override_username`/
    // `override_icon_url`), and the author id underneath is the hook's OWNER: two different
    // integrations of one owner within five minutes were drawn as ONE speaker, the second
    // with neither name nor icon (2026-10-05, Jenkins under a WhatsApp bridge post).
    const webhookBoundary = isFromWebhook(post) || (previous ? isFromWebhook(previous) : false);
    const isGroupStart =
      !sameAuthor || !withinWindow || crossesDay || showsUnread || systemBoundary || webhookBoundary;

    const label = crossesDay ? dayLabel(post.createAt, options) : null;

    result.push({ post, isGroupStart, dayLabel: label, showsUnreadSeparator: showsUnread });
  }
  return result;
}

/**
 * Substring first: this runs for every row of every page, and only a handful of posts carry
 * props at all — the JSON is parsed only when the key is there.
 */
function isFromWebhook(post: PostRecord): boolean {
  const props = post.props;
  if (!props || !props.includes('from_webhook')) return false;
  return MessageAttachments.isFromWebhook(props);
}

function isSpanish(locale: string): boolean {
  return locale.toLowerCase().startsWith('es');
}

function dayLabel(createAtMillis: number, options: MessageListOptions): string {
  const { now, timeZone } = options;
  const locale = options.locale ?? uiLocale();
  // Against `now`, never the system clock. `isDateInToday`/`isDateInYesterday` read the real
  // date, which silently made the `now` parameter a lie: production never noticed (there
  // `now` IS today) but the test only passed on the day it was written. Android has always
  // compared against the injected instant; this is iOS catching up.
  const day = dayKey(createAtMillis, timeZone);
  if (day === dayKey(now, timeZone)) return t('channel.day.today');
  if (day === shiftedDayKey(now, -1, timeZone)) return t('channel.day.yesterday');
  const c = civil(createAtMillis, timeZone);
  const sameYear = c.year === civil(now, timeZone).year;
  // Assembled by hand rather than from Intl's own date patterns so the result is exactly the
  // Swift `DateFormatter` pattern ("d 'de' MMMM['de' yyyy]" / "MMMM d[, yyyy]") whatever the
  // engine's locale data does with day+month (es-MX vs es_ES, ordering, commas).
  const month = dateFormatter(locale, { month: 'long' }, timeZone).format(new Date(createAtMillis));
  if (isSpanish(locale)) {
    return sameYear ? `${c.day} de ${month}` : `${c.day} de ${month} de ${c.year}`;
  }
  return sameYear ? `${month} ${c.day}` : `${month} ${c.day}, ${c.year}`;
}

export const MessageListDerivation = {
  groupWindowMillis,
  rows,
  isFromWebhook,
  dayLabel,
} as const;
